import React from 'react'
import { shape, string, array, func } from 'prop-types'
import { createComponent } from '@ycos/fela'
import { createFontStyle } from '@ycos/primitives'
import { BackgroundPicture } from '@ycos/picture'

const Container = createComponent(
  'Container',
  ({ theme }) => ({
    alignItems: 'center',
    cursor: 'pointer',
    display: 'flex',
    height: '100%',
    'screen-medium': {
      display: 'block',
      padding: `0 ${3 * theme.spacingMultiplier}px`
    }
  }),
  'a'
)

const Wrapper = createComponent(
  'Wrapper',
  ({ theme, brand }) => ({
    padding: brand?.site === 'theoutnet' ? `0 ${4 * theme.spacingMultiplier}px` : 'initial'
  })
)

const Columns = createComponent('Container', ({ theme, brand }) => ({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  maxWidth: '1024px',
  margin: '0 auto',
  height: brand?.site === 'theoutnet' ? '100%' : 'auto',
  'screen-medium': {
    height: '100%',
    alignItems: 'center',
    flexDirection: theme.direction === 'rtl' ? 'row-reverse' : 'row'
  }
}))

const SalePreTitleFirst = createComponent(
  'SalePreTitleFirst',
  ({ theme }) => ({
    color: theme.typography.colors.PrimaryWhite,
    fontSize: '26px',
    letterSpacing: '-1.87px',
    textTransform: 'uppercase',
    textAlign: 'center',
    ...theme.custom.saleBanner.preTitle,
    margin: 0,
    'screen-medium': {
      fontSize: '28px',
      letterSpacing: '-2.8px',
      ...theme.custom.saleBanner.preTitle,
      margin: 0
    },
    'screen-large': {
      fontSize: '38px',
      ...theme.custom.saleBanner.preTitle
    },
    'screen-xLarge': {
      fontSize: '48px'
    }
  }),
  'h2'
)

const SalePreTitleNewDesigner = createFontStyle('SalePreTitleNewDesigner', { type: 'Heading', tagName: 'h2', name: 'B4' }, ({ theme, large }) => ({
  color: theme.typography.colors.PrimaryWhite,
  fontSize: '24px',
  letterSpacing: '0px',
  textAlign: 'center',
  margin: 0,
  'screen-medium': {
    fontSize: '36px',
    marginBottom: '4px',
    marginTop: '-18px'
  },
  'screen-large': {
    marginBottom: large ? '20px' : '0px',
    marginTop: '-20px'
  },
  'screen-xLarge': {
    fontSize: '48px'
  }
}))

const SalePreTitleSixtyOff = createComponent(
  'SalePreTitleSixtyOff',
  ({ theme }) => ({
    color: theme.typography.colors.PrimaryWhite,
    fontSize: '48px',
    letterSpacing: '-3.2px',
    textTransform: 'uppercase',
    textAlign: 'center',
    ...theme.custom.saleBanner.preTitle,
    margin: '0px auto',
    maxWidth: '304px',
    lineHeight: '1',
    'screen-medium': {
      maxWidth: 'none',
      fontSize: '32px',
      letterSpacing: '-2.13px',
      ...theme.custom.saleBanner.preTitle,
      margin: 0
    },
    'screen-large': {
      fontSize: '42px',
      letterSpacing: '-2.8px',
      ...theme.custom.saleBanner.preTitle
    },
    'screen-xLarge': {
      fontSize: '48px',
      letterSpacing: '-3.2px'
    }
  }),
  'h2'
)

const SaleTitleFirst = createComponent(
  'SaleTitleFirst',
  ({ theme, small }) => ({
    color: theme.typography.colors.PrimaryWhite,
    textTransform: 'uppercase',
    fontSize: small ? '54px' : '84px',
    margin: 0,
    marginBlockEnd: small ? '-12px' : '-20px',
    letterSpacing: '-5.6px',
    overflow: 'hidden',
    textAlign: 'center',
    overflowY: 'hidden',
    ...theme.custom.saleBanner.title,
    'screen-medium': {
      fontSize: small ? '64px' : '84px',
      alignSelf: 'center',
      flex: '50%'
    },
    'screen-large': {
      fontSize: small ? '92px' : '100px',
      marginBlockEnd: '-26px',
      letterSpacing: '-6.6px',
      ...theme.custom.saleBanner.title
    }
  }),
  'h2'
)

const SaleTitleNewDesigner = createComponent(
  'SaleTitleNewDesigner',
  ({ theme }) => ({
    color: theme.typography.colors.PrimaryWhite,
    textTransform: 'uppercase',
    fontSize: '32px',
    margin: 0,
    marginBlockEnd: '4px',
    letterSpacing: '-2.13px',
    overflow: 'hidden',
    textAlign: 'center',
    overflowY: 'hidden',
    ...theme.custom.saleBanner.title,
    'screen-medium': {
      fontSize: '32px',
      alignSelf: 'center',
      flex: '50%'
    }
  }),
  'h2'
)

const SaleSubTitleNewDesigner = createComponent(
  'SaleSubTitleNewDesigner',
  ({ theme, large }) => ({
    color: theme.typography.colors.PrimaryWhite,
    textTransform: 'uppercase',
    fontSize: '32px',
    lineHeight: '1',
    margin: 0,
    marginBlockEnd: large ? '14px' : '18px',
    letterSpacing: '-2.13px',
    overflow: 'hidden',
    textAlign: 'center',
    overflowY: 'hidden',
    maxWidth: '340px',
    paddingTop: '4px',
    ...theme.custom.saleBanner.title,
    'screen-medium': {
      marginBlockEnd: '0px',
      alignSelf: 'center',
      flex: '50%'
    },
    'screen-large': {
      maxWidth: '330px'
    }
  }),
  'h2'
)

const SaleTitleSixtyOff = createComponent(
  'SaleTitleSixtyOff',
  ({ theme }) => ({
    color: theme.typography.colors.PrimaryWhite,
    textTransform: 'uppercase',
    fontSize: '32px',
    marginTop: '-48px',
    marginBlockEnd: '10px',
    letterSpacing: '-2.13px',
    overflow: 'hidden',
    textAlign: 'center',
    overflowY: 'hidden',
    ...theme.custom.saleBanner.title,
    'screen-medium': {
      fontSize: '84px',
      alignSelf: 'center',
      flex: '50%',
      letterSpacing: '-5.6px',
      marginBlockEnd: '-30px',
      margin: 0
    },
    'screen-large': {
      fontSize: '100px',
      marginBlockEnd: '-26px',
      letterSpacing: '-6.67px',
      ...theme.custom.saleBanner.title
    }
  }),
  'h2'
)

const SaleText = createFontStyle('SaleText', { type: 'Label', tagName: 'p', name: '1' }, ({ theme }) => ({
  color: theme.typography.colors.PrimaryWhite,
  fontSize: '10px',
  lineHeight: '1.5',
  margin: 0,
  textAlign: 'center',
  position: 'absolute',
  bottom: `${2 * theme.spacingMultiplier}px`,
  left: 0,
  right: 0
}))

const SaleCTA = createFontStyle('SaleCTA', { type: 'Label', tagName: 'p', name: '4' }, ({ theme }) => ({
  color: theme.typography.colors.PrimaryWhite,
  textDecoration: 'underline',
  margin: 0,
  textAlign: 'center',
  position: 'absolute',
  left: 0,
  right: 0,
  bottom: '40px',
  'screen-medium': {
    position: 'initial'
  }
}))

const SaleSubTitle = createFontStyle('SaleSubTitle', { type: 'Paragraph', tagName: 'p', name: '1' }, ({ theme }) => ({
  color: theme.typography.colors.PrimaryWhite,
  fontSize: '14px',
  margin: 0,
  textAlign: 'center',
  marginTop: `-${0.5 * theme.spacingMultiplier}px`,
  'screen-medium': {
    marginTop: `-${theme.spacingMultiplier}px`
  },
  'screen-large': {
    marginTop: `-${3 * theme.spacingMultiplier}px`
  }
}))

const RightColumn = createComponent('RightColumn', ({ large }) => ({
  flex: '1 1 50%',
  minHeight: large ? '88px' : '56px',
  'screen-medium': {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    height: '100%'
  }
}))

const LeftColumn = createComponent('RightColumn', () => ({
  flex: '1  1 50%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center'
}))

const SaleBanner = ({ item, onTargetClick, locale = {}, config = {}, brand }) => {
  const { subTitlePlain, preTitlePlain, teaserTitle, teaserTextPlain, pictures, target, dataCmMetadata, layoutVariant, fetchPriority } = item
  const { imageSphere } = config

  const SaleTitle =
    {
      'isc-sale-banner': SaleTitleFirst,
      'isc-sale-banner-new-designer': SaleTitleNewDesigner,
      'isc-sale-banner-sixty-off': SaleTitleSixtyOff
    }[layoutVariant] || SaleTitleFirst

  const SalePreTitle =
    {
      'isc-sale-banner': SalePreTitleFirst,
      'isc-sale-banner-new-designer': SalePreTitleNewDesigner,
      'isc-sale-banner-sixty-off': SalePreTitleSixtyOff
    }[layoutVariant] || SalePreTitleFirst

  const smallTitle = locale.language === 'fr'
  const largeHeight = teaserTextPlain && subTitlePlain

  const additionalStyles = {
    height: '256px',
    'screen-medium': {
      height: '172px'
    },
    'screen-large': {
      height: '194px'
    }
  }

  return (
    <Wrapper brand={brand}>
      <BackgroundPicture
        fetchPriority={fetchPriority}
        opacity={1}
        images={pictures}
        height="100%"
        opacityColor="transparent"
        additionalStyles={additionalStyles}
        imagesphere={imageSphere}
        lazy={false}
      >
        <Container data-cm-metadata={dataCmMetadata} data-testid="saleBannerLink" href={target.href} onClick={onTargetClick}>
          <Columns brand={brand}>
            <LeftColumn>
              <SaleTitle small={smallTitle}>{teaserTitle}</SaleTitle>
              {teaserTextPlain && <SaleSubTitleNewDesigner>{teaserTextPlain}</SaleSubTitleNewDesigner>}
            </LeftColumn>
            <RightColumn large={largeHeight}>
              <SalePreTitle large={largeHeight}>{preTitlePlain}</SalePreTitle>
              {subTitlePlain && <SaleSubTitle>{subTitlePlain}</SaleSubTitle>}
              <SaleCTA>{target.teaserTitle}</SaleCTA>
              <SaleText>{target.teaserTextPlain}</SaleText>
            </RightColumn>
          </Columns>
        </Container>
      </BackgroundPicture>
    </Wrapper>
  )
}

SaleBanner.layoutVariant = ['isc-sale-banner', 'isc-sale-banner-new-designer', 'isc-sale-banner-sixty-off']

SaleBanner.propTypes = {
  locale: shape({
    language: string.isRequired
  }).isRequired,
  item: shape({
    preTitlePlain: string.isRequired,
    subTitlePlain: string,
    teaserTitle: string.isRequired,
    teaserTextPlain: string.isRequired,
    pictures: array,
    layoutVariant: string.isRequired,
    target: shape({
      href: string.isRequired,
      teaserTitle: string.isRequired,
      teaserTextPlain: string.isRequired
    }).isRequired
  }),
  onTargetClick: func.isRequired
}

SaleBanner.defaultProps = {
  item: {}
}

export default SaleBanner
